import { useState, useMemo, useRef, useEffect } from 'react'
import { checkIsEventClosed } from '_hooks/useIsEventClosed'

export const TAB_KEYS = {
  ALL: 'all',
  ACTIVE: 'active',
  CLOSED: 'closed',
}

export const SORT_KEYS = {
  CLOSING: 'closing',
  COLLECTION: 'collection',
  NEWEST: 'newest',
  ACTIVE: 'active',
}

export default function useFilterBar(rawEvents = []) {
  const [activeTab, setActiveTab] = useState(TAB_KEYS.ACTIVE)
  const [currentSort, setCurrentSort] = useState(SORT_KEYS.NEWEST)
  const [isSortOpen, setIsSortOpen] = useState(false)
  const sortRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sortRef.current && !sortRef.current.contains(event.target)) {
        setIsSortOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey)
  }

  const handleSortChange = (sortKey) => {
    setCurrentSort(sortKey)
    setIsSortOpen(false)
  }

  const filteredEvents = useMemo(() => {
    if (!Array.isArray(rawEvents)) return []

    const tabFiltered = rawEvents.filter((item) => {
      const isClosed = checkIsEventClosed(item)
      if (activeTab === TAB_KEYS.ACTIVE) {
        return !isClosed
      }
      if (activeTab === TAB_KEYS.CLOSED) {
        return isClosed
      }
      return true
    })

    const sorted = [...tabFiltered]
    switch (currentSort) {
      case SORT_KEYS.CLOSING:
        return sorted.sort(
          (a, b) => (Number(a?.closingTimestamp) || 0) - (Number(b?.closingTimestamp) || 0),
        )
      case SORT_KEYS.COLLECTION:
        return sorted.sort(
          (a, b) => (Number(a?.pickupTimestamp) || 0) - (Number(b?.pickupTimestamp) || 0),
        )
      case SORT_KEYS.ACTIVE:
        return sorted.sort(
          (a, b) => (Number(b?.totalOrderCount) || 0) - (Number(a?.totalOrderCount) || 0),
        )
      case SORT_KEYS.NEWEST:
      default:
        return sorted.sort(
          (a, b) => (Number(b?.createdAt) || 0) - (Number(a?.createdAt) || 0),
        )
    }
  }, [rawEvents, activeTab, currentSort])

  return {
    filteredEvents,
    filterBarProps: {
      activeTab,
      onTabChange: handleTabChange,
      currentSort,
      onSortChange: handleSortChange,
      isSortOpen,
      setIsSortOpen,
      sortRef,
    },
  }
}
